import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client.js';
import { useToast } from '../context/ToastContext.jsx';
import { crc, fecha, fechaHora, hoy, METODOS_PAGO } from '../utils/format.js';
import { PageHeader, Modal, Loading, EmptyState } from '../components/UI.jsx';
import { Icon } from '../components/Icons.jsx';
import FilterBar from '../components/FilterBar.jsx';

function inicioMes() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

export default function CuentasPorCobrar() {
  const toast = useToast();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [totales, setTotales] = useState(null);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [q, setQ] = useState('');
  const [estadoCobro, setEstadoCobro] = useState('pendiente');
  const [soloVencidas, setSoloVencidas] = useState(false);
  const [modal, setModal] = useState(null); // venta a marcar
  const [metodoPago, setMetodoPago] = useState('transferencia');
  const [notasCobro, setNotasCobro] = useState('');
  const [montoAbono, setMontoAbono] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [cajaAbierta, setCajaAbierta] = useState(undefined);

  const cargar = () => {
    const params = {};
    if (desde) params.desde = desde;
    if (hasta) params.hasta = hasta;
    if (q) params.q = q;
    if (estadoCobro) params.estado_cobro = estadoCobro;
    if (soloVencidas) params.vencidas = '1';
    api.get('/ventas/cuentas-por-cobrar', { params }).then((r) => {
      setItems(r.data.data.items || []);
      setTotales(r.data.data.totales || null);
    }).catch((err) => {
      toast.error(err.response?.data?.message || 'No se pudieron cargar las cuentas');
      setItems([]);
    });
  };

  useEffect(cargar, [desde, hasta, q, estadoCobro, soloVencidas]);
  useEffect(() => {
    api.get('/cajas/actual').then((r) => setCajaAbierta(!!r.data.data)).catch(() => setCajaAbierta(false));
  }, []);

  const limpiar = () => {
    setDesde('');
    setHasta('');
    setQ('');
    setEstadoCobro('pendiente');
    setSoloVencidas(false);
  };

  const saldoDe = (v) => Math.max(0, Number(v?.saldo ?? (Number(v?.total) - Number(v?.monto_pagado || 0))));

  const registrarCobro = async () => {
    if (!modal) return;
    const saldo = saldoDe(modal);
    const monto = Number(montoAbono);
    if (!(monto > 0)) {
      toast.error('Indica de cuanto es el abono');
      return;
    }
    if (monto > saldo + 0.001) {
      toast.error('El abono no puede ser mayor al saldo');
      return;
    }
    if (cajaAbierta === false) {
      toast.error('No hay una caja abierta. Abra la caja para registrar el cobro.');
      return;
    }
    setGuardando(true);
    try {
      await api.patch(`/ventas/${modal.id}/cobro`, {
        estado_cobro: monto + 0.001 >= saldo ? 'pagado' : 'abono',
        metodo_pago: metodoPago,
        monto_abono: monto,
        notas_cobro: notasCobro || `Cobrado ${hoy()}`,
      });
      toast.success(monto + 0.001 >= saldo
        ? `Venta ${modal.numero} marcada como pagada`
        : `Abono de ${crc(monto)} registrado en ${modal.numero}`);
      setModal(null);
      setNotasCobro('');
      setMontoAbono('');
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudo actualizar');
    } finally {
      setGuardando(false);
    }
  };

  const marcarPendiente = async (v) => {
    if (!confirm(`¿Volver a pendiente la venta ${v.numero}?`)) return;
    try {
      await api.patch(`/ventas/${v.id}/cobro`, { estado_cobro: 'pendiente' });
      toast.success('Vuelta a pendiente');
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudo actualizar');
    }
  };

  const resumen = totales
    ? `${totales.n_pendiente} pendiente(s) · ${crc(totales.pendiente)} por cobrar · ${crc(totales.vencido)} vencido`
    : '';

  return (
    <div>
      <PageHeader
        title="Cuentas por cobrar"
        subtitle="Facturas a credito pendientes de pago o ya cobradas"
      />

      {cajaAbierta === false && (
        <div
          className="card"
          style={{
            padding: 14,
            marginBottom: 16,
            border: '1px solid var(--danger)',
            background: 'var(--danger-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <strong>Caja cerrada.</strong>
            <span className="muted" style={{ marginLeft: 6 }}>
              No se puede registrar un cobro hasta abrir el turno.
            </span>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/caja')}>
            Ir a caja
          </button>
        </div>
      )}

      <FilterBar
        desde={desde}
        hasta={hasta}
        onDesde={setDesde}
        onHasta={setHasta}
        busqueda={q}
        onBusqueda={setQ}
        showBusqueda
        placeholderBusqueda="Numero, cliente, cedula…"
        onLimpiar={limpiar}
        resumen={resumen}
      >
        <div className="field" style={{ margin: 0, minWidth: 140 }}>
          <label>Estado cobro</label>
          <select
            className="select"
            value={estadoCobro}
            onChange={(e) => {
              setEstadoCobro(e.target.value);
              if (e.target.value === 'pagado') setSoloVencidas(false);
            }}
          >
            <option value="">Todos</option>
            <option value="pendiente">Pendiente</option>
            <option value="pagado">Pagado</option>
          </select>
        </div>
        <label
          className="flex items-center gap-sm"
          style={{ cursor: 'pointer', paddingBottom: 4, fontSize: 13 }}
        >
          <input
            type="checkbox"
            checked={soloVencidas}
            disabled={estadoCobro === 'pagado'}
            onChange={(e) => setSoloVencidas(e.target.checked)}
          />
          Solo vencidas
        </label>
      </FilterBar>

      {totales && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 12,
            marginBottom: 16,
          }}
        >
          <CardStat label="Pendiente por cobrar" value={crc(totales.pendiente)} color="var(--warning, #d97706)" sub={`${totales.n_pendiente} doc.`} />
          <CardStat label="Ya cobrado" value={crc(totales.cobrado)} color="var(--success, #16a34a)" sub={`${totales.n_pagado} doc.`} />
          <CardStat label="Vencido" value={crc(totales.vencido)} color="var(--danger)" sub="Plazo superado" />
        </div>
      )}

      {!items ? (
        <Loading />
      ) : items.length === 0 ? (
        <EmptyState
          icon="ventas"
          titulo="Sin cuentas por cobrar"
          texto="Al facturar a credito en el POS, la venta aparece aqui como pendiente."
        />
      ) : (
        <div className="card table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Venta</th>
                <th>Fecha</th>
                <th>Cliente</th>
                <th>Vence</th>
                <th className="text-right">Total</th>
                <th className="text-right">Saldo</th>
                <th>Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((v) => (
                <tr
                  key={v.id}
                  style={{
                    background: v.vencida ? 'var(--warning-bg, rgba(217,119,6,0.08))' : undefined,
                  }}
                >
                  <td>
                    <div style={{ fontWeight: 600 }}>{v.numero}</div>
                    <div className="muted" style={{ fontSize: 11 }}>
                      {v.tipo_comprobante}
                      {v.plazo_credito ? ` · ${v.plazo_credito} dias` : ''}
                    </div>
                  </td>
                  <td className="muted" style={{ fontSize: 13 }}>{fechaHora(v.fecha)}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{v.cliente_nombre || '—'}</div>
                    <div className="muted mono" style={{ fontSize: 12 }}>
                      {v.cliente_identificacion || ''}
                    </div>
                  </td>
                  <td>
                    {v.fecha_vencimiento ? (
                      <span style={{ color: v.vencida ? 'var(--danger)' : undefined, fontWeight: v.vencida ? 700 : 400 }}>
                        {fecha(v.fecha_vencimiento)}
                        {v.vencida && Number(v.dias_vencido) > 0 ? (
                          <span className="muted" style={{ display: 'block', fontSize: 11 }}>
                            +{v.dias_vencido} dias
                          </span>
                        ) : null}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="text-right mono" style={{ fontWeight: 700 }}>
                    {crc(v.total)}
                    {Number(v.monto_pagado) > 0 && v.estado_cobro !== 'pagado' && (
                      <div className="muted" style={{ fontSize: 11, fontWeight: 400 }}>
                        abonado {crc(v.monto_pagado)}
                      </div>
                    )}
                  </td>
                  <td className="text-right mono" style={{ fontWeight: 700 }}>
                    {v.estado_cobro === 'pagado' ? crc(0) : crc(saldoDe(v))}
                  </td>
                  <td>
                    {v.estado_cobro === 'pagado' ? (
                      <span className="badge badge-success">Pagado</span>
                    ) : v.vencida ? (
                      <span className="badge badge-danger">Vencido</span>
                    ) : (
                      <span
                        className="badge"
                        style={{ background: 'var(--warning-bg)', color: 'var(--warning)' }}
                      >
                        Pendiente
                      </span>
                    )}
                    {v.fecha_pago && (
                      <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                        {fechaHora(v.fecha_pago)}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="flex gap-sm" style={{ justifyContent: 'flex-end' }}>
                      {v.estado_cobro === 'pendiente' ? (
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={cajaAbierta === false}
                          onClick={() => {
                            if (cajaAbierta === false) return;
                            setModal(v);
                            setMetodoPago('transferencia');
                            setNotasCobro('');
                            setMontoAbono('');
                          }}
                        >
                          <Icon.check width={14} height={14} /> Registrar cobro
                        </button>
                      ) : (
                        <button className="btn btn-outline btn-sm" onClick={() => marcarPendiente(v)}>
                          Volver a pendiente
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={!!modal}
        onClose={() => !guardando && setModal(null)}
        title={modal ? `Registrar cobro · ${modal.numero}` : 'Cobro'}
        width={420}
      >
        {modal && (
          <div>
            <p style={{ marginBottom: 12 }}>
              <strong>{modal.cliente_nombre}</strong>
              <br />
              <span className="muted">Total: </span>
              <span className="mono" style={{ fontWeight: 700 }}>{crc(modal.total)}</span>
              <span className="muted"> · Saldo: </span>
              <span className="mono" style={{ fontWeight: 700, color: 'var(--accent-texto)' }}>
                {crc(saldoDe(modal))}
              </span>
            </p>
            <div className="field">
              <label>Monto del abono</label>
              <input
                className="input"
                type="number"
                min="0.01"
                step="0.01"
                max={saldoDe(modal)}
                value={montoAbono}
                autoFocus
                onChange={(e) => setMontoAbono(e.target.value)}
              />
              {/* El campo arranca vacio a proposito: antes venia lleno con el
                  saldo completo y era facil registrar un pago total cuando el
                  cliente solo estaba abonando. Pagar todo sigue a un clic. */}
              <div className="flex items-center justify-between" style={{ marginTop: 6, gap: 8, flexWrap: 'wrap' }}>
                <span className="muted" style={{ fontSize: 12 }}>Escribi lo que el cliente abona hoy.</span>
                <button type="button" className="btn btn-ghost btn-sm"
                  onClick={() => setMontoAbono(saldoDe(modal).toFixed(2))}>
                  Pagar todo ({crc(saldoDe(modal))})
                </button>
              </div>
            </div>
            <div className="field">
              <label>Metodo de pago del cobro</label>
              <select
                className="select"
                value={metodoPago}
                onChange={(e) => setMetodoPago(e.target.value)}
              >
                {METODOS_PAGO.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Nota (opcional)</label>
              <input
                className="input"
                value={notasCobro}
                onChange={(e) => setNotasCobro(e.target.value)}
                placeholder="Ej: SINPE ref. 12345"
              />
            </div>
            <div className="flex gap-sm" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
              <button className="btn btn-outline" disabled={guardando} onClick={() => setModal(null)}>
                Cancelar
              </button>
              <button className="btn btn-primary" disabled={guardando || cajaAbierta === false} onClick={registrarCobro}>
                {guardando ? <span className="spinner" /> : (
                  Number(montoAbono) + 0.001 >= saldoDe(modal) ? 'Confirmar pagado' : 'Registrar abono'
                )}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function CardStat({ label, value, sub, color }) {
  return (
    <div className="card" style={{ padding: 14 }}>
      <div className="muted" style={{ fontSize: 12, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color, marginTop: 4 }} className="mono">
        {value}
      </div>
      {sub && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
